// MACHINERY V2 (Stroje) — catalog model detail, the 380px left column (M2.8).
//
// Spec: data package §11 (detail rows of the field table). Reference impl:
// components/CatalogModelDetailPage.tsx; artboard 0902 — image, model name,
// manufacturer breadcrumb, category badge + subtype chip, KĽÚČOVÉ PARAMETRE,
// the add CTA, and the units-on-platform footer line.
//
// The image + thumbnail strip is CatalogDetailGallery.tsx (300-line cap); the
// hero-vs-`images` rule and its blast radius are documented there. It is KEYED
// by model id below for the same reason CatalogDetailV2 keys the spec sheet:
// the detail route reuses one element across `/catalog/:modelId` changes, so a
// gallery holding "thumbnail 4 selected" would carry that index into the next
// model and render the "No photo" placeholder for a model that HAS photos.
//
// >>> `unitsOnPlatform` HAS A REAL ZERO STATE. <<<
// Artboard 0902 writes "zatiaľ 0 ks na platforme — budeš prvý" under the CTA,
// which is the existing `noUnitsYet` key ("Be the first to add this machine").
// v1 already branches on the same count; the field is now TYPED
// (models.ts) instead of read through `(model as any)`.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { formatManufacturerWithHierarchy } from "../../../../shared/utils/machineUtils";
import { CatalogModelDetail } from "../../models/models";
import { CapabilityBadges } from "../readiness/CapabilityBadge";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import { CatalogChip, CategoryBadge } from "./catalogAtoms";
import CatalogDetailGallery from "./CatalogDetailGallery";
import { extractKeySpecs } from "./catalogKeySpecs";

export interface CatalogDetailAsideProps {
  model: CatalogModelDetail;
  presentation: MachineryV2ReadinessPresentation;
  canWrite: boolean;
  onAdd: () => void;
  onOpenReadiness: () => void;
}

const CatalogDetailAside: React.FC<CatalogDetailAsideProps> = ({
  model,
  presentation,
  canWrite,
  onAdd,
  onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");

  const keySpecs = useMemo(() => extractKeySpecs(model.specSections), [model.specSections]);
  const units = model.unitsOnPlatform || 0;
  const manufacturerLine = model.manufacturer
    ? formatManufacturerWithHierarchy(
        model.manufacturer.name,
        model.manufacturer.parentManufacturerName
      )
    : "";

  return (
    <aside
      style={{
        width: 380,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
      }}
    >
      <div
        style={{
          borderRadius: 10,
          border: "1px solid var(--border)",
          background: "var(--card)",
          boxShadow: "var(--shadow)",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <CatalogDetailGallery key={model.id} model={model} />

        <div style={{ padding: "12px 14px 14px", minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--ink)",
              minWidth: 0,
              wordBreak: "break-word",
            }}
          >
            {model.modelName}
          </h2>
          {manufacturerLine && (
            <div style={{ marginTop: 3, fontSize: 12.5, color: "var(--muted)", minWidth: 0 }}>
              {manufacturerLine}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginTop: 9,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <CategoryBadge code={model.categoryCode} />
            {model.subType && <CatalogChip mono={false}>{model.subType}</CatalogChip>}
            {model.cncBrand && <CatalogChip>{model.cncBrand}</CatalogChip>}
          </div>

          {/* The readiness surface on the detail page — same badges, same
              popup, same "connected = static" rule as the card. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              marginTop: 11,
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            <CapabilityBadges
              presentation={presentation}
              size={22}
              onOpenDetails={onOpenReadiness}
            />
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                color: presentation.hasAnyDocs ? "var(--muted)" : "var(--faint)",
                minWidth: 0,
              }}
            >
              {presentation.summary.docPart || translate("myMachinery:midasNoDocsYet")}
            </span>
          </div>
        </div>
      </div>

      {keySpecs.length > 0 && (
        <div
          style={{
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: "var(--card)",
            boxShadow: "var(--shadow)",
            padding: "12px 14px",
            minWidth: 0,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            {translate("myMachinery:keySpecs")}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            {/* Index key: `extractKeySpecs` preserves v1's behaviour of letting
                two aliases ("Spindle Speed" / "Max Spindle Speed") resolve to
                the SAME spec, so labels are not guaranteed unique. The list is
                order-stable and never reordered, so the index is safe. */}
            {keySpecs.map((spec, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${spec.label}-${index}`}
                style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12.5,
                    color: "var(--muted)",
                    textTransform: "capitalize",
                    wordBreak: "break-word",
                  }}
                >
                  {spec.label}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11.5,
                    color: "var(--ink)",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {spec.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The CTA is Write-gated (§11); the units line is NOT — how many of a
          model are on the platform is read-only information a Read user is
          entitled to, and hiding it with the button would drop a binding. */}
      <div style={{ minWidth: 0 }}>
        {canWrite && (
          <button
            type="button"
            className="btn btn-mod"
            onClick={onAdd}
            style={{ width: "100%", height: 38, justifyContent: "center", fontSize: 13 }}
          >
            <span aria-hidden style={{ fontSize: 16, lineHeight: 1, marginRight: 4 }}>
              +
            </span>
            {translate("myMachinery:addThisMachine")}
          </button>
        )}
        <div
          style={{
            marginTop: canWrite ? 6 : 0,
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            letterSpacing: "0.04em",
            color: units > 0 ? "var(--green)" : "var(--faint)",
            minWidth: 0,
          }}
        >
          {units > 0
            ? translate("myMachinery:unitsOnPlatform", { count: units })
            : translate("myMachinery:noUnitsYet")}
        </div>
      </div>
    </aside>
  );
};

export default CatalogDetailAside;
